import React, { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import * as XLSX from "xlsx" // Excel解析用
import { useLoading } from "../../LoadingContext"
import DashboardLayout from "../../components/DashboardLayout"
import DashboardNavbar from "../../components/DashboardNavbar"
import MDBox from "components/MDBox"
import MDTypography from "components/MDTypography"
import MDButton from "components/MDButton"
import { manageBulkImport } from "../../../api/manage_bulk_import"
import { Card, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper } from "@mui/material"
import { getContractInformation } from "../../../api/get_contract_information"

// --- 型定義 ---

interface AccountData {
  name: string
  mail: string
}

interface FacilityData {
  name: string
  address: string
  phone: string
  building_type: number
}

interface ResidentData {
  facility_name: string
  room_no: string
  name: string
  initial: string
  birthday: number
  phone: string
  is_poc: boolean
  serials: string[]
  places: string[]
}

interface ImportData {
  estate: {
    name: string
    address: string
    phone: string
    accounts: AccountData[]
  }
  facilities: FacilityData[]
  residents: ResidentData[]
}

// バリデーション結果保持用
interface ValidationResult {
  estate: { name?: string; address?: string; phone?: string }
  accounts: { [key: number]: { name?: string; mail?: string } }
  facilities: { [key: number]: { name?: string; address?: string; phone?: string; building_type?: string } }
  residents: {
    [key: number]: {
      room_no?: string
      name?: string
      initial?: string
      birthday?: string
      phone?: string
      facility_not_found?: string
    }
  }
  hasError: boolean
}

const emojiRegex = /(?:\p{Extended_Pictographic}(?![\u3190-\u319F\u3300-\u33FF]))+/gu

const DataImportPage: React.FC = () => {
  const navigate = useNavigate()
  const { startLoading, stopLoading } = useLoading()
  const [previewData, setPreviewData] = useState<ImportData | null>(null)
  const [validation, setValidation] = useState<ValidationResult>({
    estate: {},
    accounts: {},
    facilities: {},
    residents: {},
    hasError: false
  })
  const [errors, setErrors] = useState({
    format: "",
    server: ""
  })
  const [contractInformationUrl, setContractInformationUrl] = useState<string>("")

  useEffect(() => {
    const fetchInfo = async () => {
      try {
        startLoading()

        //契約時資料のダウンロードリンク
        const contractInformation = await getContractInformation()
        setContractInformationUrl(contractInformation.url_data)
      } finally {
        stopLoading()
      }
    }
    fetchInfo()
  }, [])

  // ヘルパー関数 ---
  const getCellValue = (sheet: XLSX.WorkSheet, address: string) => {
    const cell = sheet[address]
    return cell ? String(cell.v).trim() : ""
  }

  // バリデーション関数
  const validateParsedData = (data: ImportData): ValidationResult => {
    const res: ValidationResult = {
      estate: {},
      accounts: {},
      facilities: {},
      residents: {},
      hasError: false
    }

    // 1. 不動産業者バリデーション
    if (!data.estate.name) {
      res.estate.name = "必須項目です"
      res.hasError = true
    } else if (data.estate.name.length > 64) {
      res.estate.name = "64文字以内です"
      res.hasError = true
    }

    if (!data.estate.address) {
      res.estate.address = "必須項目です"
      res.hasError = true
    } else if (data.estate.address.length > 256) {
      res.estate.address = "256文字以内です"
      res.hasError = true
    }

    if (!data.estate.phone) {
      res.estate.phone = "必須項目です"
      res.hasError = true
    } else if (data.estate.phone.length > 14) {
      res.estate.phone = "14文字以内です"
      res.hasError = true
    }

    // 2. アカウントバリデーション
    data.estate.accounts.forEach((acc, i) => {
      res.accounts[i] = {}
      if (!acc.name) {
        res.accounts[i].name = "必須"
        res.hasError = true
      } else if (acc.name.length > 12) {
        res.accounts[i].name = "12文字以内"
        res.hasError = true
      } else if (emojiRegex.test(acc.name)) {
        res.accounts[i].name = "絵文字不可"
        res.hasError = true
      }

      if (!acc.mail) {
        res.accounts[i].mail = "必須"
        res.hasError = true
      } else if (acc.mail.length > 64) {
        res.accounts[i].mail = "64文字以内"
        res.hasError = true
      } else if (emojiRegex.test(acc.mail)) {
        res.accounts[i].mail = "絵文字不可"
        res.hasError = true
      }
    })

    // 3. 施設バリデーション
    data.facilities.forEach((fac, i) => {
      res.facilities[i] = {}
      if (!fac.name) {
        res.facilities[i].name = "必須項目です"
        res.hasError = true
      } else if (fac.name.length > 64) {
        res.facilities[i].name = "64文字以内です"
        res.hasError = true
      }

      if (!fac.address) {
        res.facilities[i].address = "必須項目です"
        res.hasError = true
      } else if (fac.address.length > 256) {
        res.facilities[i].address = "256文字以内です"
        res.hasError = true
      }

      if (fac.phone && fac.phone.length > 14) {
        res.facilities[i].phone = "14文字以内です"
        res.hasError = true
      }

      if (fac.building_type === undefined || fac.building_type === null) {
        res.facilities[i].building_type = "選択してください"
        res.hasError = true
      }
    })

    // 4. 入居者バリデーション
    const facilityNames = data.facilities.map((f) => f.name)
    data.residents.forEach((r, i) => {
      res.residents[i] = {}

      // 施設名の存在確認
      if (!facilityNames.includes(r.facility_name)) {
        res.residents[i].facility_not_found = "施設リストに存在しません"
        res.hasError = true
      }

      if (!r.room_no) {
        res.residents[i].room_no = "必須"
        res.hasError = true
      } else if (r.room_no.length > 5) {
        res.residents[i].room_no = "5文字以内"
        res.hasError = true
      } else if (emojiRegex.test(r.room_no)) {
        res.residents[i].room_no = "絵文字不可"
        res.hasError = true
      }

      if (!r.name) {
        res.residents[i].name = "必須"
        res.hasError = true
      } else if (r.name.length > 12) {
        res.residents[i].name = "12文字以内"
        res.hasError = true
      } else if (emojiRegex.test(r.name)) {
        res.residents[i].name = "絵文字不可"
        res.hasError = true
      }

      if (!r.initial) {
        res.residents[i].initial = "必須"
        res.hasError = true
      } else if (r.initial.length > 4) {
        res.residents[i].initial = "4文字以内"
        res.hasError = true
      } else if (emojiRegex.test(r.initial)) {
        res.residents[i].initial = "絵文字不可"
        res.hasError = true
      }

      if (!r.birthday) {
        res.residents[i].birthday = "必須"
        res.hasError = true
      } else if (r.birthday.toString().length !== 6) {
        res.residents[i].birthday = "6文字（YYYYMM）"
        res.hasError = true
      }

      if (!r.phone) {
        res.residents[i].phone = "必須"
        res.hasError = true
      } else if (r.phone.length > 14) {
        res.residents[i].phone = "14文字以内"
        res.hasError = true
      } else if (emojiRegex.test(r.phone)) {
        res.residents[i].phone = "絵文字不可"
        res.hasError = true
      }
    })

    return res
  }

  // --- イベントハンドラ ---

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    // 【重要】inputの値をリセット
    // これにより、同じファイル名を選択しても onChange が再度発火するようになる
    e.target.value = ""

    // 【追加】新しいファイルの読み込みを開始するので、以前のプレビューとバリデーション状態をクリア
    setPreviewData(null)
    setValidation({
      estate: {},
      accounts: {},
      facilities: {},
      residents: {},
      hasError: false
    })

    startLoading()
    const reader = new FileReader()

    reader.onload = (evt) => {
      try {
        const bstr = evt.target?.result
        const wb = XLSX.read(bstr, { type: "binary" })
        const sheetName = "初期登録情報"
        const ws = wb.Sheets[sheetName]

        if (!ws) {
          setErrors({ format: `シート「${sheetName}」が見つかりません。`, server: "" })
          return
        }

        // 1. 不動産業者
        const estate = {
          name: getCellValue(ws, "E4"),
          address: getCellValue(ws, "E5") + getCellValue(ws, "E6"),
          phone: getCellValue(ws, "E7"),
          accounts: [] as AccountData[]
        }
        for (let i = 0; i < 5; i++) {
          const name = getCellValue(ws, `E${8 + i * 2}`)
          const mail = getCellValue(ws, `E${9 + i * 2}`)
          if (name || mail) estate.accounts.push({ name, mail })
        }

        // 2. 施設
        const facilities = []
        for (let i = 0; i < 3; i++) {
          const offset = i * 5
          const name = getCellValue(ws, `E${18 + offset}`)
          const addr = getCellValue(ws, `E${19 + offset}`) + getCellValue(ws, `E${20 + offset}`)
          const phone = getCellValue(ws, `E${21 + offset}`)
          const typeStr = getCellValue(ws, `E${22 + offset}`)

          if (name || addr || phone) {
            facilities.push({
              name,
              address: addr,
              phone,
              building_type: typeStr === "マンション" ? 0 : typeStr === "" ? -1 : 1
            })
          }
        }

        // 3. 入居者
        const residents = []
        let currentRow = 33
        while (getCellValue(ws, `E${currentRow}`) || getCellValue(ws, `E${currentRow + 2}`)) {
          residents.push({
            facility_name: getCellValue(ws, `E${currentRow}`),
            room_no: getCellValue(ws, `E${currentRow + 1}`),
            name: getCellValue(ws, `E${currentRow + 2}`),
            initial: getCellValue(ws, `E${currentRow + 3}`),
            birthday: parseInt(getCellValue(ws, `E${currentRow + 4}`)),
            phone: getCellValue(ws, `E${currentRow + 5}`),
            is_poc: false, //getCellValue(ws, `E${currentRow + 6}`) === "○",
            serials: [
              getCellValue(ws, `E${currentRow + 6}`),
              getCellValue(ws, `E${currentRow + 7}`),
              getCellValue(ws, `E${currentRow + 8}`)
            ].filter((v) => v !== ""),
            places: [
              getCellValue(ws, `E${currentRow + 9}`),
              getCellValue(ws, `E${currentRow + 10}`),
              getCellValue(ws, `E${currentRow + 11}`)
            ].filter((v) => v !== "")
          })
          currentRow += 12
          if (currentRow > 1000) break
        }

        const parsedData = { estate, facilities, residents }
        setPreviewData(parsedData)
        setValidation(validateParsedData(parsedData))
        setErrors({ format: "", server: "" })
      } catch (error) {
        console.log(error)
        setErrors({ format: "Excelの解析に失敗しました。形式を確認してください。", server: "" })
      } finally {
        stopLoading()
      }
    }

    reader.readAsBinaryString(file)
  }

  const handleImport = async () => {
    if (!previewData || validation.hasError) return
    try {
      startLoading()
      setErrors({ format: "", server: "" })
      const response = await manageBulkImport(previewData)
      if (response.success === 1) {
        navigate("/home")
      } else {
        setErrors({ format: "", server: response.message })
      }
    } catch (error: any) {
      console.error(error)
      setErrors({ format: "", server: "通信エラーが発生しました。" })
    } finally {
      stopLoading()
    }
  }

  // --- 表示用コンポーネント ---

  const ErrorMsg = ({ msg }: { msg?: string }) =>
    msg ? (
      <MDTypography variant="caption" color="error" display="block" sx={{ fontWeight: "bold" }}>
        {msg}
      </MDTypography>
    ) : null

  return (
    <DashboardLayout>
      <MDBox mb={4}>
        <DashboardNavbar absolute={true} />
      </MDBox>
      <MDBox pt={3} pb={3}>
        <MDTypography variant="h4">初期データ一括登録</MDTypography>
      </MDBox>

      <Card sx={{ p: 3, mb: 3 }}>
        <MDBox
          display="flex"
          justifyContent="space-between"
          alignItems="center" // 上下の中心を合わせる
          mb={2}
        >
          <MDTypography variant="h6" style={{ margin: 0 }}>
            Excelファイルをアップロード
          </MDTypography>

          {contractInformationUrl !== "" && (
            <a
              href={contractInformationUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{ fontSize: "0.875rem", textDecoration: "none" }}
            >
              フォーマットをダウンロード
            </a>
          )}
        </MDBox>
        <input type="file" accept=".xlsx, .xls" onChange={handleFileChange} />
        <MDTypography variant="caption" color="text" display="block" mt={1}>
          ※「初期登録情報」シートを読み込みます。
        </MDTypography>
        {errors.format && (
          <MDBox
            color="#FF0000" // テキスト色
            mt={1} // 上マージン
            fontSize="0.7em" // フォントサイズ
          >
            {errors.format}
          </MDBox>
        )}
      </Card>

      {previewData && (
        <>
          {/* 1. 不動産業者プレビュー */}
          <Card sx={{ p: 3, mb: 3 }}>
            <MDTypography variant="h6" gutterBottom>
              【不動産業者】
            </MDTypography>
            <MDBox mb={2}>
              <MDTypography variant="body2" sx={{ fontSize: "0.85rem" }}>
                業者名: {previewData.estate.name}
                <ErrorMsg msg={validation.estate.name} />
              </MDTypography>
              <MDTypography variant="body2" sx={{ fontSize: "0.85rem" }}>
                住所: {previewData.estate.address}
                <ErrorMsg msg={validation.estate.address} />
              </MDTypography>
              <MDTypography variant="body2" sx={{ fontSize: "0.85rem" }}>
                電話: {previewData.estate.phone}
                <ErrorMsg msg={validation.estate.phone} />
              </MDTypography>
            </MDBox>

            <MDTypography variant="subtitle2" sx={{ fontSize: "0.8rem", borderTop: "1px solid #eee", pt: 1 }}>
              登録アカウント:
            </MDTypography>
            <MDBox display="flex" flexWrap="wrap">
              {previewData.estate.accounts.map((acc, i) => (
                <MDBox key={i} mr={3} mb={1} p={1} sx={{ border: "1px dashed #ccc", borderRadius: 1 }}>
                  <MDTypography variant="caption" display="block">
                    名前: {acc.name} <ErrorMsg msg={validation.accounts[i]?.name} />
                  </MDTypography>
                  <MDTypography variant="caption" display="block">
                    メール: {acc.mail} <ErrorMsg msg={validation.accounts[i]?.mail} />
                  </MDTypography>
                </MDBox>
              ))}
            </MDBox>
          </Card>

          {/* 2. 施設プレビュー */}
          <Card sx={{ p: 3, mb: 3 }}>
            <MDTypography variant="h6" gutterBottom>
              【施設】
            </MDTypography>
            <MDBox display="flex" flexWrap="wrap">
              {previewData.facilities.map((fac, i) => (
                <MDBox
                  key={i}
                  mr={2}
                  mb={1}
                  p={1}
                  sx={{ border: "1px solid #eee", borderRadius: 1, minWidth: "200px" }}
                >
                  <MDTypography variant="body2" fontWeight="bold" sx={{ fontSize: "0.8rem" }}>
                    {fac.name}
                  </MDTypography>
                  <ErrorMsg msg={validation.facilities[i]?.name} />
                  <MDTypography variant="caption" display="block">
                    住所: {fac.address}
                  </MDTypography>
                  <ErrorMsg msg={validation.facilities[i]?.address} />
                  <MDTypography variant="caption" display="block">
                    電話: {fac.phone}
                  </MDTypography>
                  <ErrorMsg msg={validation.facilities[i]?.phone} />
                  <MDTypography variant="caption" display="block">
                    種別: {fac.building_type === 0 ? "マンション" : "アパート"}
                  </MDTypography>
                  <ErrorMsg msg={validation.facilities[i]?.building_type} />
                </MDBox>
              ))}
            </MDBox>
          </Card>

          {/* 3. 入居者一覧プレビュー */}
          <Card sx={{ p: 3, mb: 3 }}>
            <MDTypography variant="h6" mb={2}>
              【入居者登録プレビュー】 ({previewData.residents.length}件)
            </MDTypography>
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead sx={{ display: "table-header-group" }}>
                  <TableRow>
                    <TableCell sx={{ fontSize: "0.75rem", fontWeight: "bold" }}>施設名</TableCell>
                    <TableCell sx={{ fontSize: "0.75rem", fontWeight: "bold" }}>部屋</TableCell>
                    <TableCell sx={{ fontSize: "0.75rem", fontWeight: "bold" }}>氏名/イニシャル/生年月</TableCell>
                    <TableCell sx={{ fontSize: "0.75rem", fontWeight: "bold" }}>電話番号</TableCell>
                    <TableCell sx={{ fontSize: "0.75rem", fontWeight: "bold" }}>デバイス</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {previewData.residents.map((r, i) => (
                    <TableRow
                      key={i}
                      sx={{ backgroundColor: validation.residents[i]?.facility_not_found ? "#fff0f0" : "inherit" }}
                    >
                      <TableCell sx={{ fontSize: "0.75rem" }}>
                        {r.facility_name}
                        <ErrorMsg msg={validation.residents[i]?.facility_not_found} />
                      </TableCell>
                      <TableCell sx={{ fontSize: "0.75rem" }}>
                        {r.room_no}
                        <ErrorMsg msg={validation.residents[i]?.room_no} />
                      </TableCell>
                      <TableCell sx={{ fontSize: "0.75rem" }}>
                        {r.name} ({r.initial}) {r.birthday}
                        <ErrorMsg msg={validation.residents[i]?.name} />
                        <ErrorMsg msg={validation.residents[i]?.initial} />
                        <ErrorMsg msg={validation.residents[i]?.birthday} />
                      </TableCell>
                      <TableCell sx={{ fontSize: "0.75rem" }}>
                        {r.phone}
                        <ErrorMsg msg={validation.residents[i]?.phone} />
                      </TableCell>
                      <TableCell sx={{ fontSize: "0.75rem" }}>{r.serials.join(", ")}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Card>

          {errors.server && (
            <MDBox
              color="#FF0000" // テキスト色
              mb={2} // 下マージン
              fontSize="0.7em" // フォントサイズ
            >
              {errors.server}
            </MDBox>
          )}

          {/* 登録ボタン */}
          <MDBox mt={2}>
            {validation.hasError && (
              <MDTypography variant="body2" color="error" textAlign="center" mb={1} sx={{ fontWeight: "bold" }}>
                ※入力データに不備があるため、登録できません。Excelの内容を修正してください。
              </MDTypography>
            )}
            <MDButton
              variant="contained"
              color={validation.hasError ? "secondary" : "info"}
              fullWidth
              onClick={handleImport}
              disabled={validation.hasError}
            >
              この内容で一括登録を実行する
            </MDButton>
          </MDBox>
        </>
      )}
    </DashboardLayout>
  )
}

export default DataImportPage
